import dailyproductionService from '../../../services/dailyproductionService';
import filterService from '../../../services/filterService';

import {
    setDailyProductionLoading,
    setDailyProductionError,
    setDailyProductionList,
    setDailyProductionCurrent,
    setDailyProductionPagination,
    setDailyProductionSort,
    setDailyProductionFilters,
    setDailyProductionDefaultFilter,
    setDailyProductionSettings,
    clearDailyProductionCurrent,
    clearDailyProductionError,
    setDailyProductionFilterLoading,
    changeDailyProductionFilterCondition,
    addDailyProductionFilterCondition,
    updateDailyProductionFilterConditionRule,
    updateDailyProductionFilterConditionValue,
    removeDailyProductionFilterCondition,
    setDailyProductionSelected,
    resetDailyProductionFiltersAndPagination
} from './dailyproductionSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().dailyproduction.isSettingsInitialized) {
            dispatch(setDailyProductionLoading(true));
            const response = await dailyproductionService.getSettings();

            // Set the settings
            dispatch(setDailyProductionSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setDailyProductionDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setDailyProductionError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setDailyProductionLoading(false));
    }
};

export const fetchDailyproductions = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().dailyproduction;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setDailyProductionLoading(true));
        const response = await dailyproductionService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setDailyProductionList(response));

        // total is deferred when not cached server-side: load it after the rows are shown
        if (response?.meta && response.meta.total === undefined) {
            dailyproductionService.getCount(finalQueryParams, finalActiveFilter)
                .then((total) => {
                    // ignore a bad/missing total so the estimate (and Next button) stays
                    if (!Number.isFinite(Number(total))) return;
                    const perPage = Number(finalQueryParams.per_page) || 10;
                    dispatch(setDailyProductionPagination({ total, last_page: Math.max(1, Math.ceil(total / perPage)) }));
                })
                .catch(() => { });
        }
        return response;
    } catch (error) {
        console.error('Error in fetchDailyproductions:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setDailyProductionError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setDailyProductionLoading(false));
    }
};

export const fetchDailyProduction = (id) => async (dispatch) => {
    try {
        dispatch(setDailyProductionLoading(true));
        const response = await dailyproductionService.getOne(id);
        dispatch(setDailyProductionCurrent(response.data));
    } catch (error) {
        dispatch(setDailyProductionError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setDailyProductionLoading(false));
    }
};

export const createDailyProduction = (data) => async (dispatch) => {
    try {
        const response = await dailyproductionService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateDailyProduction = (id, data) => async (dispatch) => {
    try {
        const response = await dailyproductionService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteDailyProduction = (id) => async (dispatch) => {
    try {
        dispatch(setDailyProductionLoading(true));
        const response = await dailyproductionService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setDailyProductionError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setDailyProductionLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setDailyProductionPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setDailyProductionSort(sort));
};

export const searchDailyproductions = (query) => (dispatch, getState) => {
    const currentFilters = getState().dailyproduction.filters;
    dispatch(setDailyProductionFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setDailyProductionDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeDailyProductionFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addDailyProductionFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateDailyProductionFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateDailyProductionFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeDailyProductionFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setDailyProductionFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().dailyproduction.settings;
            dispatch(setDailyProductionSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setDailyProductionFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setDailyProductionFilterLoading(false));
        dispatch(setDailyProductionError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setDailyProductionSelected(selectedRows));
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetDailyProductionFiltersAndPagination());
};